// Порядок шагов подключения кошелька TON — БЕЗ axios и без самой библиотеки TON Connect, чтобы его
// можно было прогнать зондом на фейках (`tools/ton-connect-flow-check.mjs`, браузер не нужен).
//
// 🔴 ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ: каждый шаг здесь может ЗАВИСНУТЬ НАВСЕГДА, а любое зависание игрок видит
// как «кнопка привязки перестала работать»: экран поднял флаг «ждём подтверждения» и никогда его не
// опустит, потому что промис не резолвится и не реджектится. Три известных источника:
//   1. `disconnect()` уходит по мосту в приложение кошелька и не отвечает, пока игрок его не закроет
//      (жалоба оунера 2026-08-22: после отказа привязки кнопка мертва до закрытия кошелька);
//   2. игрок закрыл окно выбора кошелька — статус не меняется, ошибки нет, ждать нечего и некого;
//   3. кошелёк открыт, подтверждение не нажимают — ждём бесконечно.
// Поэтому у КАЖДОГО ожидания здесь есть выход по времени и понятный код ошибки для экрана.

export const DISCONNECT_MS = 4000;          // сколько ждём разрыва прежнего подключения
export const CONNECT_MS = 3 * 60 * 1000;    // потолок ожидания подтверждения в кошельке
// 🔴 Окно выбора закрылось, а события выбора кошелька не было — ждём ещё столько, прежде чем счесть
// это отменой. Двадцать секунд, а не мгновение: у части кошельков (расширение в браузере) окно
// закрывается ДО подтверждения, а событие выбора может и не прийти — короткая пауза обрывала бы
// нормальное подключение. Реальная отмена стоит игроку этих секунд ожидания и только.
export const CANCEL_GRACE_MS = 20000;

/** Ждём промис не дольше `ms`; по истечении отдаём `mark` вместо того, чтобы висеть. */
export function withTimeout(promise, ms, mark = 'timeout') {
  let timer = null;
  const guard = new Promise((resolve) => { timer = setTimeout(() => resolve(mark), ms); });
  // Провал самого промиса тут не важен: нас интересует только «дождались или нет».
  const safe = Promise.resolve(promise).then((v) => v, () => 'error');
  return Promise.race([safe, guard]).finally(() => { if (timer) clearTimeout(timer); });
}

/**
 * Разорвать прежнее подключение. `true` — можно продолжать, `false` — кошелёк не отпустил мост
 * (обычно приложение кошелька открыто и держит сессию).
 */
export async function dropConnection(ui, ms = DISCONNECT_MS) {
  if (!ui || !ui.connected) return true;
  const r = await withTimeout(Promise.resolve(ui.disconnect()), ms);
  return r !== 'timeout';
}

/**
 * Дождаться подключённого кошелька. Отклоняется кодами `cancelled` (игрок закрыл окно выбора),
 * `timeout` (не подтвердил) или ошибкой самой библиотеки.
 */
// 🔴 ОДНО ОЖИДАНИЕ КОШЕЛЬКА НА ВКЛАДКУ (жалоба оунера 2026-08-23: «окно выбора перестало
// открываться, что-то быстро мелькает»). Прежний вызов ждёт кошелёк ДО ТРЁХ МИНУТ и всё это время
// держит подписки библиотеки. Второе нажатие заводило вторую пару подписок на ОДИН модальный
// менеджер: окно открывалось и тут же гасилось обработчиком первого вызова. Поэтому новый проход
// сперва отменяет прежний и закрывает окно — «мелькание» это ровно он и есть.
let activeWait = null;

export function waitForWallet(ui, opts = {}) {
  const graceMs = opts.cancelGraceMs === undefined ? CANCEL_GRACE_MS : opts.cancelGraceMs;
  const timeoutMs = opts.connectMs === undefined ? CONNECT_MS : opts.connectMs;
  // Библиотека шлёт свои события на `window`; в зонде сюда передают фейковый источник.
  const events = opts.events === undefined ? (typeof window === 'undefined' ? null : window) : opts.events;
  if (activeWait) { try { activeWait(); } catch (e) { /* прежний уже завершён */ } }
  return new Promise((resolve, reject) => {
    let done = false;
    let picked = false;   // игрок УЖЕ выбрал кошелёк и ушёл подтверждать
    let timer = null;
    const offs = [];
    const finish = (ok, value) => {
      if (done) return;
      done = true;
      if (activeWait === cancel) activeWait = null;
      if (timer) clearTimeout(timer);
      offs.forEach((off) => { try { if (typeof off === 'function') off(); } catch (e) { /* уже отписаны */ } });
      (ok ? resolve : reject)(value);
    };
    // Отмена «сверху»: игрок начал новый проход, прежний обязан отпустить окно и подписки.
    const cancel = () => {
      if (done) return;
      if (typeof ui.closeModal === 'function') { try { ui.closeModal(); } catch (e) { /* окна уже нет */ } }
      finish(false, new Error('superseded'));
    };
    activeWait = cancel;
    offs.push(ui.onStatusChange(
      (w) => { if (w) finish(true, w); },
      (err) => finish(false, err instanceof Error ? err : new Error('wallet-error')),
    ));
    const on = (name, fn) => {
      if (!events || typeof events.addEventListener !== 'function') return;
      events.addEventListener(name, fn);
      offs.push(() => { try { events.removeEventListener(name, fn); } catch (e) { /* уже сняты */ } });
    };
    // 🔴 ПРИЗНАК «КОШЕЛЁК ВЫБРАН» — СОБЫТИЕ БИБЛИОТЕКИ, А НЕ ПРИЧИНА ЗАКРЫТИЯ ОКНА (жалоба оунера
    // 2026-08-22: на ПК в Яндекс.Браузере с расширением MyTonWallet подключиться было нельзя).
    // Расширение подтверждает в СВОЁМ окне, а окно выбора библиотека закрывает сама — и `closeReason`
    // там приходит «отмена» (это ЗНАЧЕНИЕ ПО УМОЛЧАНИЮ у программного закрытия). Мы принимали это за
    // отказ игрока и обрывали подключение молча, пока он подтверждал в расширении.
    const onPicked = () => { picked = true; };
    on('ton-connect-ui-selected-wallet', onPicked);
    on('ton-connect-ui-connection-started', onPicked);
    // Кошелёк отказал (игрок нажал «отклонить» в расширении) — отпускаем кнопку сразу, не по таймауту.
    on('ton-connect-ui-connection-error', () => finish(false, new Error('cancelled')));
    if (typeof ui.onModalStateChange === 'function') {
      offs.push(ui.onModalStateChange((st) => {
        if (!st || st.status !== 'closed') return;
        // «Кошелёк выбран» — окно закрылось само, игрок ушёл подтверждать: это не отмена.
        if (st.closeReason === 'wallet-selected' || picked) return;
        // Успешное подключение тоже закрывает окно, поэтому решаем не сразу.
        setTimeout(() => {
          if (!ui.connected && !picked) finish(false, new Error('cancelled'));
        }, graceMs);
      }));
    }
    timer = setTimeout(() => finish(false, new Error('timeout')), timeoutMs);
    Promise.resolve(ui.openModal()).catch((err) => {
      finish(false, err instanceof Error ? err : new Error('wallet-error'));
    });
  });
}

// Сколько ждём, пока библиотека восстановит мост к уже подключённому кошельку. Она делает это
// АСИНХРОННО при старте страницы, и обращение к `ui.connected` раньше времени врёт «не подключён».
export const RESTORE_MS = 8000;
// Потолок ожидания подписи операции. Отдельно от подключения: здесь окно кошелька уже открыто.
export const SEND_MS = 3 * 60 * 1000;

/**
 * Гарантировать ПОДКЛЮЧЁННЫЙ кошелёк перед отправкой операции (обмен $ENRG).
 *
 * 🔴 БЕЗ ЭТОГО КОШЕЛЁК «НЕ ОТКРЫВАЕТСЯ» (жалоба оунера 2026-08-23: «из игры не получается обменять,
 * кошелёк не открывается для подтверждения»). Причины две, и обе невидимы с экрана:
 *   1. привязка кошелька в ИГРЕ и подключение в БИБЛИОТЕКЕ — разные вещи. Привязка живёт на сервере
 *      вечно, а мост TON Connect рвётся сам (закрыл браузер, истекла сессия, отключил расширение);
 *   2. мост восстанавливается асинхронно — если спросить `ui.connected` сразу после загрузки
 *      страницы, ответ «нет», хотя через мгновение кошелёк будет на месте.
 * В обоих случаях `sendTransaction` бросает «кошелёк не подключён» — и это выглядит как «кнопка
 * ничего не делает».
 */
export async function ensureWallet(ui, opts = {}) {
  const restoreMs = opts.restoreMs === undefined ? RESTORE_MS : opts.restoreMs;
  if (ui.connectionRestored) await withTimeout(ui.connectionRestored, restoreMs);
  // 🔴 Признак живого моста — АККАУНТ, а не флаг `connected`. Библиотека держит флаг по сохранённой
  // сессии, и он остаётся `true`, когда моста уже нет: тогда `sendTransaction` падает, а `openModal`
  // мигает («подключаться не к чему, вы уже подключены»).
  if (ui.wallet && ui.wallet.account) return ui.wallet;
  if (ui.connected && ui.account) return { account: ui.account };
  // 🔴 САМИ ОКНО НЕ ОТКРЫВАЕМ (жалоба оунера 2026-08-23: «Тонкипер предложил привязать кошелёк, а не
  // подписать транзакцию»). Подключение и подпись — РАЗНЫЕ шаги, и подменять один другим нельзя:
  // игрок нажал «Обменять», а получил экран привязки — он не понимает, что подтверждает, и закрывает
  // кошелёк. Отдаём понятный код: экран покажет отдельную кнопку «Подключить кошелёк».
  throw new Error('not-connected');
}

/**
 * Явное подключение кошелька ДЛЯ ПОДПИСИ (кнопкой игрока, а не тайком внутри операции).
 * Без запроса `ton_proof`: привязка к аккаунту уже есть, здесь нужен только живой мост.
 */
export async function connectForSend(ui, opts = {}) {
  // 🔴 СПЕРВА РВЁМ ПРЕЖНЕЕ ПОДКЛЮЧЕНИЕ (жалоба оунера 2026-08-23: «окно быстро мигает и ничего не
  // происходит»). У библиотеки может числиться ЖИВОЕ подключение, мост которого давно мёртв: сессия
  // истекла, приложение кошелька закрыто, расширение отключено. В этом состоянии `openModal()`
  // открывает окно и ТУТ ЖЕ закрывает его — выбирать нечего, кошелёк «уже подключён». Снаружи это и
  // выглядит как мигание. Ровно та же грабля закрыта в `runConnect` при ПРИВЯЗКЕ — там разрыв стоит
  // с самого начала, поэтому привязка работала, а обмен нет.
  const dropMs = opts.disconnectMs === undefined ? DISCONNECT_MS : opts.disconnectMs;
  await dropConnection(ui, dropMs);
  // 🔴 Сбрасываем параметры подключения: с прошлой ПРИВЯЗКИ там мог остаться запрос `ton_proof` с
  // истёкшим кодом, и кошелёк отказал бы в подключении, не объяснив причины.
  if (typeof ui.setConnectRequestParameters === 'function') ui.setConnectRequestParameters(null);
  return waitForWallet(ui, opts);
}

/**
 * Отправить операцию с выходом по времени. Зависший промис экран видит как «ещё ждём» и держит
 * кнопку выключенной навсегда — то самое правило, из-за которого написан весь этот файл.
 */
export async function sendWithTimeout(ui, tx, ms = SEND_MS) {
  // 🔴 ТЕКСТ ОШИБКИ КОШЕЛЬКА НЕ ГЛОТАЕМ (грабля 2026-08-23). Раньше любая ошибка превращалась в
  // «вы отменили», и настоящая причина не доходила ни до игрока, ни до нас. А причина бывает
  // говорящей: библиотека САМА отклоняет запрос до кошелька, если адрес получателя в сыром виде
  // («SendTransactionRequest validation failed») — окно мелькает и гаснет, а мы три круга искали
  // проблему в мосте.
  let failed = null;
  const attempt = Promise.resolve()
    .then(() => ui.sendTransaction(tx))
    .catch((err) => { failed = err; return 'error'; });
  const r = await withTimeout(attempt, ms, 'timeout');
  if (r === 'timeout') throw new Error('timeout');
  if (r === 'error') {
    const text = String((failed && (failed.message || failed.name)) || '');
    // Явный отказ игрока в кошельке — свой код: это не ошибка, о ней говорим мягко.
    // 🔴 Слов у отказа НЕСКОЛЬКО и они зависят от кошелька: `UserRejectsError` у библиотеки,
    // «declined» у Тонкипера, «cancel»/«denied» у расширений. Ловим все — иначе игрок, нажавший
    // «отклонить», получит сырой текст ошибки и решит, что что-то сломалось.
    if (/reject|declin|cancel|denied/i.test(text) || /UserReject/i.test(String((failed && failed.name) || ''))) {
      throw new Error('cancelled');
    }
    const e = new Error(text || 'wallet-error');
    e.walletText = text;   // экран покажет ЭТУ строку: она единственная объясняет отказ
    throw e;
  }
  return r;
}

/**
 * Полный проход привязки: рвём прежнее подключение → берём одноразовый код у сервера → ждём
 * кошелёк → отдаём доказательство серверу. `requestPayload` и `submitProof` приходят снаружи —
 * здесь нет ни axios, ни сети.
 */
export async function runConnect({ ui, requestPayload, submitProof, opts = {} }) {
  const dropMs = opts.disconnectMs === undefined ? DISCONNECT_MS : opts.disconnectMs;
  // 🔴 Прежнее подключение рвём ПЕРЕД запросом кода: у подключённого кошелька окно подтверждения
  // не открывается, `ton_proof` не появляется, и привязка молча зависает на «ждём подпись».
  if (!(await dropConnection(ui, dropMs))) throw new Error('wallet-busy');

  const payload = await requestPayload();
  ui.setConnectRequestParameters({ state: 'ready', value: { tonProof: payload } });

  let wallet;
  try {
    wallet = await waitForWallet(ui, opts);
  } catch (e) {
    await dropConnection(ui, dropMs);
    throw e;
  }

  const proof = wallet && wallet.connectItems && wallet.connectItems.tonProof;
  // Кошелёк вправе не отдать доказательство (старая версия, отказ игрока) — тогда привязки нет.
  if (!proof || !proof.proof) {
    await dropConnection(ui, dropMs);
    throw new Error('no-proof');
  }

  try {
    return await submitProof(wallet, proof.proof);
  } catch (e) {
    // 🔴 Сервер отказал (например, кошелёк уже привязан к другому игроку) — подключение в библиотеке
    // остаётся ЖИВЫМ. Не разорвав его здесь, мы оставляем игрока с мёртвой кнопкой: следующее
    // нажатие упирается в подключённый кошелёк, окно подтверждения не открывается.
    await dropConnection(ui, dropMs);
    throw e;
  }
}
